import React, { useState, useEffect, useRef } from 'react';
import { ref, get, push, set } from 'firebase/database';
import { database } from '../../firebase/config';
import SignatureCanvas from 'react-signature-canvas';
import { uploadImmagine } from '../../utils/cloudinaryUtils';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { pdf } from '@react-pdf/renderer';
import { formattaData } from '../../utils/dateUtils';

// ─────────────────────────────────────────────
// STILI PDF
// ─────────────────────────────────────────────
const pdfStyles = StyleSheet.create({
  page: { padding: 20, fontSize: 8, fontFamily: 'Helvetica' },
  intestazione: { flexDirection: 'row', alignItems: 'center', marginBottom: 8, paddingBottom: 6, borderBottom: '1 solid #ccc' },
  logoImg: { width: 60, height: 24, objectFit: 'contain' },
  recapiti: { marginLeft: 10, fontSize: 7, color: '#333' },
  recapitiNome: { fontSize: 8, fontWeight: 'bold', marginBottom: 1 },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 },
  headerCell: { fontSize: 9 },
  headerCellBold: { fontSize: 9, fontWeight: 'bold' },
  title: { fontSize: 12, fontWeight: 'bold', textAlign: 'center', marginBottom: 8 },
  divider: { borderBottom: '1 solid #aaa', marginVertical: 4 },
  sectionTitle: { fontSize: 8, fontWeight: 'bold', backgroundColor: '#e0e0e0', padding: 2, marginBottom: 2 },
  row: { flexDirection: 'row', marginBottom: 2 },
  label: { fontWeight: 'bold', width: 100 },
  value: { flex: 1 },
  orarioRow: { flexDirection: 'row', gap: 12, marginBottom: 2 },
  orarioBlock: { flexDirection: 'row' },
  footer: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 8, paddingTop: 6, borderTop: '1 solid #aaa' },
  footerLabel: { fontWeight: 'bold', marginBottom: 1 },
  firmaBox: { marginTop: 6 },
  firmaImg: { width: 160, height: 60, border: '1 solid #999' },
});

// ─────────────────────────────────────────────
// COMPONENTE PDF
// ─────────────────────────────────────────────
function GiornalieraPDF({ dati }) {
  const { nomeDipendente, data, pausaInizio, pausaFine, lavori, oreTotali } = dati;
  const lavoriValidi = lavori.filter(l => (l.cantiereId || l.lavoroEseguito || l.oraInizio) && l.oraFine);

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.intestazione}>
          <Image src="CRG_logo.png" style={pdfStyles.logoImg} />
          <View style={pdfStyles.recapiti}>
            <Text style={pdfStyles.recapitiNome}>C.R.G. Scavi e trasporti Sagl</Text>
            <Text>Via Livio 5, 6830 Chiasso TI</Text>
            <Text>Tel +41 78 751 32 51 - email: info@crgscavi.ch</Text>
          </View>
        </View>

        <Text style={pdfStyles.title}>RAPPORTO GIORNALIERO</Text>

        <View style={pdfStyles.headerRow}>
          <Text style={pdfStyles.headerCellBold}>{nomeDipendente}</Text>
          <Text style={pdfStyles.headerCell}>{formattaData(data)}</Text>
        </View>

        <View style={pdfStyles.divider} />

        {lavoriValidi.map((lavoro, i) => (
          <View key={i}>
            <Text style={pdfStyles.sectionTitle}>Lavoro {i + 1}</Text>
            <View style={pdfStyles.row}>
              <Text style={pdfStyles.label}>Mezzo:</Text>
              <Text style={pdfStyles.value}>{lavoro.veicoloLabel || '-'}</Text>
            </View>
            <View style={pdfStyles.row}>
              <Text style={pdfStyles.label}>Lavoro eseguito:</Text>
              <Text style={pdfStyles.value}>{lavoro.lavoroEseguito || '-'}</Text>
            </View>
            <View style={pdfStyles.row}>
              <Text style={pdfStyles.label}>Cliente:</Text>
              <Text style={pdfStyles.value}>{lavoro.clienteLabel || '-'}</Text>
            </View>
            <View style={pdfStyles.row}>
              <Text style={pdfStyles.label}>Cantiere:</Text>
              <Text style={pdfStyles.value}>{lavoro.cantiereLabel || '-'}</Text>
            </View>
            <View style={pdfStyles.orarioRow}>
              <View style={pdfStyles.orarioBlock}>
                <Text style={pdfStyles.label}>Ora inizio: </Text>
                <Text>{lavoro.oraInizio || '-'}</Text>
              </View>
              <View style={pdfStyles.orarioBlock}>
                <Text style={pdfStyles.label}>Ora fine: </Text>
                <Text>{lavoro.oraFine || '-'}</Text>
              </View>
            </View>
            <View style={pdfStyles.divider} />
          </View>
        ))}

        <View style={pdfStyles.footer}>
          <View>
            {dati.assenza ? (
              <Text style={{ fontSize: 14, fontWeight: 'bold' }}>{dati.assenza}</Text>
            ) : (
              <>
                <Text style={pdfStyles.footerLabel}>Pausa pranzo</Text>
                <Text>Inizio {pausaInizio} - Fine {pausaFine}</Text>
              </>
            )}
          </View>
          <View>
            <Text style={pdfStyles.footerLabel}>Ore totali lavorate</Text>
            <Text style={{ fontSize: 14, fontWeight: 'bold' }}>{oreTotali}</Text>
          </View>
        </View>

        <View style={{ marginTop: 20, borderTop: '1 solid #aaa', paddingTop: 10 }}>
          <Text style={pdfStyles.footerLabel}>Firma dipendente</Text>
          <Text style={{ marginBottom: 6 }}>{nomeDipendente}</Text>
          {dati.firmaBase64
            ? <Image src={dati.firmaBase64} style={pdfStyles.firmaImg} />
            : <Text style={{ color: '#999' }}>Non firmato</Text>
          }
        </View>
      </Page>
    </Document>
  );
}

// ─────────────────────────────────────────────
// HELPER: calcola ore totali
// LOGICA PAUSA:
//   La pausa di 1 ora (12:00-13:00) viene detratta SOLO SE almeno uno dei lavori
//   copre un orario compreso tra le 12:01 e le 12:59.
//   Esempi:
//     #1: 8:00-18:00 → copre 12:01-12:59 → detrai 1h → 9h
//     #2: 8:00-12:00 → NON copre 12:01-12:59 → nessuna detrazione → 4h
//     #3: 8:00-12:15 → copre 12:01-12:15 → detrai 1h → 3h 15min
// ─────────────────────────────────────────────
function calcolaOreTotali(lavori) {
  let totMinuti = 0;

  // Soglia pausa: 12:01 = 721 minuti, 12:59 = 779 minuti
  const PAUSA_SOGLIA_INIZIO = 12 * 60 + 1;  // 721 min = 12:01
  const PAUSA_SOGLIA_FINE   = 12 * 60 + 59; // 779 min = 12:59
  const PAUSA_DURATA        = 60;            // 60 minuti

  let detraiPausa = false;

  lavori.forEach(l => {
    if (l.oraInizio && l.oraFine) {
      const [h1, m1] = l.oraInizio.split(':').map(Number);
      const [h2, m2] = l.oraFine.split(':').map(Number);
      const ini = h1 * 60 + m1;
      const fin = h2 * 60 + m2;
      const diff = fin - ini;
      if (diff > 0) totMinuti += diff;

      // Controlla se questo lavoro copre almeno un minuto tra 12:01 e 12:59
      // Un lavoro "copre" la fascia se: ini < 12:59 AND fin > 12:01
      if (ini < PAUSA_SOGLIA_FINE && fin > PAUSA_SOGLIA_INIZIO) {
        detraiPausa = true;
      }
    }
  });

  if (detraiPausa) {
    totMinuti = Math.max(0, totMinuti - PAUSA_DURATA);
  }

  if (totMinuti === 0) return '0h 0min';
  const ore = Math.floor(totMinuti / 60);
  const min = totMinuti % 60;
  return `${ore}h ${min === 0 ? '' : min + 'min'}`.trim();
}

// ─────────────────────────────────────────────
// HELPER: giorno lavorativo precedente
// ─────────────────────────────────────────────
function getPrecedenteGiornoLavorativo(dataStringa) {
  const d = new Date(dataStringa + 'T12:00:00');
  const giorno = d.getDay();
  let offset = 1;
  if (giorno === 1) offset = 3;
  else if (giorno === 0) offset = 2;
  d.setDate(d.getDate() - offset);
  return d.toISOString().split('T')[0];
}

// ─────────────────────────────────────────────
// FORM PRINCIPALE
// ─────────────────────────────────────────────
const lavoriVuoti = Array(5).fill(null).map(() => ({
  veicoloId: '', lavoroEseguito: '', oraInizio: '', oraFine: '', clienteId: '', cantiereId: ''
}));

function CaricaGiornaliera({ dipendente, onBack }) {
  const oggi = new Date().toISOString().split('T')[0];
  const precedente = getPrecedenteGiornoLavorativo(oggi);
  const [dataSelezionata, setDataSelezionata] = useState(oggi);

  const [cantieri, setCantieri] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [veicoli, setVeicoli] = useState([]);
  const [lavori, setLavori] = useState(lavoriVuoti.map(l => ({ ...l })));
  const [pausaInizio] = useState('12:00');
  const [pausaFine] = useState('13:00');
  const [saving, setSaving] = useState(false);
  const [giornaliereOggi, setGiornaliereOggi] = useState([]);
  const [giornalierePrecedente, setGiornalierePrecedente] = useState([]);
  const [showFirma, setShowFirma] = useState(false);
  const [firmaBase64, setFirmaBase64] = useState(null);
  const sigRef = useRef(null);
  const [isFerie, setIsFerie] = useState(false);
  const [isMalattia, setIsMalattia] = useState(false);

  useEffect(() => { caricaDati(); }, [dipendente]);

  async function caricaDati() {
    if (!dipendente) return;
    const [cantSnap, cliSnap, veiSnap, giorSnap] = await Promise.all([
      get(ref(database, 'cantieri')),
      get(ref(database, 'clienti')),
      get(ref(database, 'veicoli')),
      get(ref(database, 'giornaliera')),
    ]);
    if (cantSnap.exists()) setCantieri(Object.entries(cantSnap.val()).map(([k, v]) => ({ id: k, ...v })));
    if (cliSnap.exists()) setClienti(Object.entries(cliSnap.val()).map(([k, v]) => ({ id: k, ...v })));
    if (veiSnap.exists()) setVeicoli(Object.entries(veiSnap.val()).map(([k, v]) => ({ id: k, ...v })));
    if (giorSnap.exists()) {
      const tutte = Object.keys(giorSnap.val()).map(k => ({ id: k, ...giorSnap.val()[k] }));
      const delDipendente = tutte.filter(g => g.dipendenteId === dipendente.id && g.tipo === 'form');
      setGiornaliereOggi(delDipendente.filter(g => g.data === oggi));
      setGiornalierePrecedente(delDipendente.filter(g => g.data === precedente));
    }
  }

  function updateLavoro(index, field, value) {
    if (field === 'oraInizio' || field === 'oraFine') {
      const lavoriCorrente = [...lavori];
      lavoriCorrente[index] = { ...lavoriCorrente[index], [field]: value };

      const corrente = lavoriCorrente[index];
      if (corrente.oraInizio && corrente.oraFine) {
        const [h1, m1] = corrente.oraInizio.split(':').map(Number);
        const [h2, m2] = corrente.oraFine.split(':').map(Number);
        const ini = h1 * 60 + m1;
        const fin = h2 * 60 + m2;

        if (fin <= ini) {
          alert("⏰ L'ora di fine deve essere successiva all'ora di inizio.");
          lavoriCorrente[index] = { ...lavoriCorrente[index], [field]: '' };
          setLavori(lavoriCorrente);
          return;
        }

        for (let j = 0; j < lavoriCorrente.length; j++) {
          if (j === index) continue;
          const altro = lavoriCorrente[j];
          if (!altro.oraInizio || !altro.oraFine) continue;
          const [ah1, am1] = altro.oraInizio.split(':').map(Number);
          const [ah2, am2] = altro.oraFine.split(':').map(Number);
          const aIni = ah1 * 60 + am1;
          const aFin = ah2 * 60 + am2;
          if (ini < aFin && fin > aIni) {
            alert('⚠️ Questo orario lavoro si sta sovrapponendo ad un altro.');
            lavoriCorrente[index] = { ...lavoriCorrente[index], [field]: '' };
            setLavori(lavoriCorrente);
            return;
          }
        }
      }

      if (field === 'oraFine' && index < lavoriCorrente.length - 1) {
        lavoriCorrente[index + 1] = {
          ...lavoriCorrente[index + 1],
          oraInizio: value,
        };
      }

      setLavori(lavoriCorrente);
      return;
    }

    setLavori(prev => {
      const copia = [...prev];
      copia[index] = { ...copia[index], [field]: value };
      if (field === 'clienteId') copia[index].cantiereId = '';
      return copia;
    });
  }

  function getLabelVeicolo(v) {
    return `${v.marca} ${v.modello} - ${v.targa}`;
  }

  function getCantieriFiltrati(clienteId) {
    return clienteId ? cantieri.filter(c => c.clienteId === clienteId) : cantieri;
  }

  function confermaFirma() {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      setFirmaBase64(sigRef.current.toDataURL('image/png'));
      setShowFirma(false);
    }
  }

  function cancellaPad() {
    sigRef.current?.clear();
  }


function dataURLToFile(dataUrl, filename) {
  if (!dataUrl || typeof dataUrl !== 'string') {
    throw new Error('FIRMA_DATAURL_ASSENTE');
  }

  const parts = dataUrl.split(',');
  if (parts.length !== 2) {
    throw new Error('FIRMA_FORMATO_NON_VALIDO');
  }

  const mimeMatch = parts[0].match(/data:(.*?);base64/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const binary = atob(parts[1]);
  const len = binary.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new File([bytes], filename, { type: mime });
}


  async function handleSalva() {
    const staAssente = isFerie || isMalattia;

    if (!staAssente) {
      const lavoriCompilati = lavori.filter(l => l.cantiereId || l.lavoroEseguito || l.oraInizio);
      if (lavoriCompilati.length === 0) {
        alert('Compila almeno una sezione lavoro.');
        return;
      }
    }

    const giornaliereDataSelezionata = dataSelezionata === oggi ? giornaliereOggi : giornalierePrecedente;
    if (giornaliereDataSelezionata.length >= 1) {
      alert(`Hai già compilato la giornaliera per il ${formattaData(dataSelezionata)}.`);
      return;
    }

    try {
      setSaving(true);

      const nomeDipendente = dipendente
        ? `${dipendente.nome} ${dipendente.cognome}`
        : '';

      const oreTotali = staAssente
        ? '0h 0min'
        : calcolaOreTotali(lavori);

      const lavoriConLabel = lavori.map(l => ({
        ...l,
        veicoloLabel: l.veicoloId
          ? getLabelVeicolo(veicoli.find(v => v.id === l.veicoloId))
          : '',
        clienteLabel: l.clienteId
          ? clienti.find(c => c.id === l.clienteId)?.ragioneSociale || ''
          : '',
        cantiereLabel: l.cantiereId
          ? cantieri.find(c => c.id === l.cantiereId)?.luogo || ''
          : '',
      }));

      let firmaUrl = null;
      let firmaPubblicId = null;
      let firmaUploadWarning = null;

      if (firmaBase64) {
        let uploadStep = 'INIT';

        try {
          uploadStep = 'FIRMA_BASE64_TO_FILE';
          const firmaFile = dataURLToFile(firmaBase64, `firma-${Date.now()}.png`);

          uploadStep = 'FIRMA_UPLOAD_CLOUDINARY';
          const { imageUrl, publicId } = await uploadImmagine(firmaFile, 'firme');

          firmaUrl = imageUrl;
          firmaPubblicId = publicId;
        } catch (uploadErr) {
          const isStandalone =
            window.matchMedia('(display-mode: standalone)').matches ||
            window.navigator.standalone === true;

          const connectionType =
            navigator.connection?.effectiveType ||
            navigator.connection?.type ||
            'n/d';

          try {
            const nomeDipendenteLog = dipendente
              ? `${dipendente.nome} ${dipendente.cognome}`
              : 'Sconosciuto';
            const now = new Date();

            const logRecord = {
              data: now.toISOString().slice(0, 10),
              ora: now.toTimeString().slice(0, 8),
              dipendenteId: dipendente?.id || null,
              dipendente: nomeDipendenteLog,
              tipo: 'Giornaliera',
              errore: uploadErr.code || uploadErr.name || 'FIRMA_UPLOAD_WARNING',
              dettaglio: `${uploadStep ? `[${uploadStep}] ` : ''}${uploadErr.message || 'Errore upload firma'}`,
              severity: 'warning',
              appVersion: process.env.REACT_APP_VERSION || 'n/d',
              userAgent: navigator.userAgent || 'n/d',
              platform: navigator.platform || 'n/d',
              lingua: navigator.language || 'n/d',
              online: typeof navigator.onLine === 'boolean' ? navigator.onLine : 'n/d',
              isStandalone,
              connectionType,
              firmaUploadFailed: true,
              createdAt: Date.now(),
            };

            const logRef = push(ref(database, 'error_logs'));
            await set(logRef, logRecord);
          } catch (logErr) {
            console.warn('Log warning firma fallito:', logErr.message);
          }

          firmaUploadWarning = uploadErr.message || 'Upload firma non riuscito';
          firmaUrl = null;
          firmaPubblicId = null;
        }
      }

      const datiPDF = {
        nomeDipendente,
        data: dataSelezionata,
        pausaInizio: staAssente ? '-' : pausaInizio,
        pausaFine: staAssente ? '-' : pausaFine,
        lavori: lavoriConLabel,
        oreTotali,
        firmaBase64: firmaUrl,
        assenza: staAssente
          ? (isFerie ? 'FERIE' : 'MALATTIA')
          : null,
      };

      const blob = await pdf(<GiornalieraPDF dati={datiPDF} />).toBlob();

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `giornaliera-${dataSelezionata}-${nomeDipendente.replace(/\s+/g, '_')}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);

      const record = {
        tipo: 'form',
        dipendenteId: dipendente.id,
        data: dataSelezionata,
        nomeDipendente,
        pausaInizio,
        pausaFine,
        oreTotali,
        assenza: datiPDF.assenza,
        lavori: lavoriConLabel,
        firmaUrl,
        firmaPubblicId,
        createdAt: Date.now(),
      };

      const newRef = push(ref(database, 'giornaliera'));
      await set(newRef, record);

      if (firmaUploadWarning) {
        alert(
          'Giornaliera salvata e PDF scaricato.\n\nAttenzione: la firma non è stata caricata.\nDettaglio: ' +
          firmaUploadWarning
        );
      } else {
        alert('Giornaliera salvata e PDF scaricato!');
      }

      onBack();
    } catch (err) {
      // ── LOG ERRORE AUTOMATICO ──────────────────────────
      try {
        const nomeDipendente = dipendente
          ? `${dipendente.nome} ${dipendente.cognome}`
          : 'Sconosciuto';
        const now = new Date();

        const isStandalone =
          window.matchMedia('(display-mode: standalone)').matches ||
          window.navigator.standalone === true;

        const connectionType =
          navigator.connection?.effectiveType ||
          navigator.connection?.type ||
          'n/d';

        const logRecord = {
          data: now.toISOString().slice(0, 10),
          ora: now.toTimeString().slice(0, 8),
          dipendenteId: dipendente?.id || null,
          dipendente: nomeDipendente,
          tipo: 'Giornaliera', // oppure 'Bolla'
          errore: err.code || err.name || 'ERRORE_GENERICO',
          dettaglio: `${err.uploadStep ? `[${err.uploadStep}] ` : ''}${err.message || 'Errore sconosciuto'}`,
          severity: 'critical',
          appVersion: process.env.REACT_APP_VERSION || 'n/d',
          userAgent: navigator.userAgent || 'n/d',
          platform: navigator.platform || 'n/d',
          lingua: navigator.language || 'n/d',
          online: typeof navigator.onLine === 'boolean' ? navigator.onLine : 'n/d',
          isStandalone,
          connectionType,
          createdAt: Date.now(),
        };

        const logRef = push(ref(database, 'error_logs'));
        await set(logRef, logRecord);
      } catch (logErr) {
        console.warn('Log errore fallito:', logErr.message);
      }
      // ───────────────────────────────────────────────────
      alert('Errore durante il salvataggio. Il problema è stato registrato automaticamente.\n\nDettaglio: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  const giornaliereDataSelezionata = dataSelezionata === oggi ? giornaliereOggi : giornalierePrecedente;
  const giaCompilata = giornaliereDataSelezionata.length >= 1;

  return (
    <>
      <div className="dashboard-container">
        {/* SIDEBAR */}
        <div className="sidebar">
          <h2><img src="CRG_logo.png" alt="crg" style={{ width: 150 }} /></h2>
          <div className="user-info">
            <p>{dipendente ? `${dipendente.nome} ${dipendente.cognome}` : ''}</p>
          </div>
          <button className="btn-logout" onClick={onBack}>Torna indietro</button>
        </div>

        {/* CONTENUTO */}
        <div className="main-content">
          <div className="content-header">
            <h1>Rapporto Giornaliero</h1>
            <p style={{ color: '#7f8c8d', marginTop: 4 }}>Data: {formattaData(dataSelezionata)}</p>
            <div style={{ marginTop: 8 }}>
              <label style={{ fontSize: 13, color: '#555', marginRight: 8, fontWeight: 600 }}>Giorno:</label>
              <select
                value={dataSelezionata}
                onChange={e => setDataSelezionata(e.target.value)}
                style={{
                  padding: '4px 10px', borderRadius: 6, border: '1px solid #bbb',
                  fontSize: 14, background: '#fff', cursor: 'pointer'
                }}
              >
                <option value={oggi}>📅 Oggi ({formattaData(oggi)})</option>
                <option value={precedente} disabled={giornalierePrecedente.length >= 1}>
                  ◀ Ieri lavorativo ({formattaData(precedente)}){giornalierePrecedente.length >= 1 ? ' — già compilata' : ''}
                </option>
              </select>
            </div>
          </div>

          <div className="content-body">
            {giaCompilata ? (
              <div className="upload-card">
                <h3>✅ Giornaliera già compilata</h3>
                <p style={{ color: '#7f8c8d', marginTop: 8 }}>
                  Hai già inviato il rapporto giornaliero per il {formattaData(dataSelezionata)}.
                </p>
              </div>
            ) : (
              <div className="form-bolla">

                {/* ── FERIE / MALATTIA ── */}
                <div style={{
                  marginBottom: 24, padding: 16,
                  border: '2px solid #f0ad4e', borderRadius: 8,
                  background: '#fffbf0'
                }}>
                  <h3 style={{ marginBottom: 12, fontSize: 15, color: '#2c3e50' }}>Assenza</h3>
                  <div style={{ display: 'flex', gap: 32 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 15 }}>
                      <input
                        type="checkbox"
                        checked={isFerie}
                        onChange={e => {
                          setIsFerie(e.target.checked);
                          if (e.target.checked) setIsMalattia(false);
                        }}
                        style={{ width: 18, height: 18 }}
                      />
                      🏖️ Ferie
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 15 }}>
                      <input
                        type="checkbox"
                        checked={isMalattia}
                        onChange={e => {
                          setIsMalattia(e.target.checked);
                          if (e.target.checked) setIsFerie(false);
                        }}
                        style={{ width: 18, height: 18 }}
                      />
                      🤒 Malattia
                    </label>
                  </div>
                  {(isFerie || isMalattia) && (
                    <p style={{ marginTop: 10, color: '#c0392b', fontWeight: 600, fontSize: 13 }}>
                      ⚠️ Compilazione giornaliera disabilitata — verrà registrata con 0 ore lavorative.
                    </p>
                  )}
                </div>

                <fieldset disabled={isFerie || isMalattia} style={{
                  border: 'none', padding: 0, margin: 0,
                  opacity: (isFerie || isMalattia) ? 0.4 : 1,
                  pointerEvents: (isFerie || isMalattia) ? 'none' : 'auto'
                }}>

                  {/* 5 SEZIONI LAVORO */}
                  {lavori.map((lavoro, i) => {
                    const oraInizioAutomatica = i > 0;

                    return (
                      <div key={i} style={{ marginBottom: 24, padding: 16, border: '1px solid #ddd', borderRadius: 8, background: '#fafafa' }}>
                        <h3 style={{ marginBottom: 12, color: '#2c3e50', fontSize: 15 }}>Lavoro {i + 1}</h3>

                        {/* MEZZO */}
                        <div className="form-group">
                          <label>Mezzo di lavoro</label>
                          <select value={lavoro.veicoloId} onChange={e => updateLavoro(i, 'veicoloId', e.target.value)}>
                            <option value="">— Nessun mezzo —</option>
                            {veicoli.map(v => (
                              <option key={v.id} value={v.id}>{getLabelVeicolo(v)}</option>
                            ))}
                          </select>
                        </div>

                        {/* LAVORO ESEGUITO */}
                        <div className="form-group">
                          <label>Lavoro eseguito</label>
                          <textarea
                            rows={2}
                            placeholder="Descrivi il lavoro svolto..."
                            value={lavoro.lavoroEseguito}
                            onChange={e => updateLavoro(i, 'lavoroEseguito', e.target.value)}
                          />
                        </div>

                        {/* ORARI */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>

                          {/* ORA INIZIO */}
                          <div className="form-group">
                            <label>
                              Ora inizio
                              {oraInizioAutomatica && (
                                <span style={{
                                  marginLeft: 8, fontSize: 11, background: '#e8f5e9',
                                  color: '#2e7d32', borderRadius: 4, padding: '1px 6px', fontWeight: 600
                                }}>
                                  automatico
                                </span>
                              )}
                            </label>
                            {oraInizioAutomatica ? (
                              <input
                                type="text"
                                value={lavoro.oraInizio || '—'}
                                readOnly
                                style={{
                                  background: '#e8f5e9',
                                  border: '1px solid #a5d6a7',
                                  borderRadius: 6,
                                  padding: '8px 12px',
                                  fontSize: 14,
                                  color: '#2e7d32',
                                  fontWeight: 600,
                                  cursor: 'not-allowed',
                                  width: '100%',
                                }}
                              />
                            ) : (
                              <select
                                value={lavoro.oraInizio}
                                onChange={e => updateLavoro(i, 'oraInizio', e.target.value)}
                              >
                                <option value="">-- Seleziona --</option>
                                {Array.from({ length: (22 * 60 - 5 * 60) / 5 + 1 }, (_, idx) => {
                                  const totalMin = 5 * 60 + idx * 5;
                                  const hh = String(Math.floor(totalMin / 60)).padStart(2, '0');
                                  const mm = String(totalMin % 60).padStart(2, '0');
                                  return <option key={`ini-${idx}`} value={`${hh}:${mm}`}>{hh}:{mm}</option>;
                                })}
                              </select>
                            )}
                          </div>

                          {/* ORA FINE */}
                          <div className="form-group">
                            <label>Ora fine</label>
                            <select
                              value={lavoro.oraFine}
                              onChange={e => updateLavoro(i, 'oraFine', e.target.value)}
                            >
                              <option value="">-- Seleziona --</option>
                              {Array.from({ length: (22 * 60 - 5 * 60) / 5 + 1 }, (_, idx) => {
                                const totalMin = 5 * 60 + idx * 5;
                                const hh = String(Math.floor(totalMin / 60)).padStart(2, '0');
                                const mm = String(totalMin % 60).padStart(2, '0');
                                return <option key={`fin-${idx}`} value={`${hh}:${mm}`}>{hh}:{mm}</option>;
                              })}
                            </select>
                          </div>

                        </div>

                        {/* CLIENTE */}
                        <div className="form-group">
                          <label>Cliente</label>
                          <select value={lavoro.clienteId} onChange={e => updateLavoro(i, 'clienteId', e.target.value)}>
                            <option value="">— Seleziona cliente —</option>
                            {clienti.map(c => (
                              <option key={c.id} value={c.id}>{c.ragioneSociale}</option>
                            ))}
                          </select>
                        </div>

                        {/* CANTIERE */}
                        <div className="form-group">
                          <label>Cantiere</label>
                          <select value={lavoro.cantiereId} onChange={e => updateLavoro(i, 'cantiereId', e.target.value)}>
                            <option value="">— Seleziona cantiere —</option>
                            {getCantieriFiltrati(lavoro.clienteId).map(c => (
                              <option key={c.id} value={c.id}>{c.luogo}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    );
                  })}

                  {/* PAUSA PRANZO */}
                  <div style={{ marginBottom: 24, padding: 16, border: '1px solid #ddd', borderRadius: 8, background: '#fff9e6' }}>
                    <h3 style={{ marginBottom: 12, fontSize: 15, color: '#2c3e50' }}>Pausa pranzo</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div className="form-group">
                        <label>Inizio pausa</label>
                        <input
                          type="time"
                          value={pausaInizio}
                          readOnly
                          style={{ background: '#f0f0f0', cursor: 'not-allowed', color: '#555' }}
                        />
                      </div>
                      <div className="form-group">
                        <label>Fine pausa</label>
                        <input
                          type="time"
                          value={pausaFine}
                          readOnly
                          style={{ background: '#f0f0f0', cursor: 'not-allowed', color: '#555' }}
                        />
                      </div>
                    </div>
                    <p style={{ marginTop: 8, fontSize: 12, color: '#888' }}>⏱ Pausa fissa 12:00 – 13:00 (detratta solo se si lavora oltre le 12:00)</p>
                  </div>

                  {/* ORE TOTALI */}
                  <div style={{ marginBottom: 24, padding: 14, background: '#e3f2fd', borderRadius: 8, textAlign: 'right' }}>
                    <span style={{ fontWeight: 700, fontSize: 16 }}>
                      Ore totali stimate: {calcolaOreTotali(lavori)}
                    </span>
                  </div>
                </fieldset>

                {/* FIRMA DIPENDENTE */}
                <div style={{ marginBottom: 24, padding: 16, border: '1px solid #ddd', borderRadius: 8, background: '#f9f9f9' }}>
                  <div className="firma-row">
                    <div className="firma-col">
                      <label>Nome dipendente</label>
                      <p className="autista-name">{dipendente ? `${dipendente.nome} ${dipendente.cognome}` : ''}</p>
                    </div>
                    <div className="firma-col">
                      <label>Firma dipendente</label>
                      {firmaBase64 ? (
                        <div>
                          <img src={firmaBase64} alt="Firma" className="firma-preview" />
                          <button className="btn-secondary small" onClick={() => setFirmaBase64(null)} style={{ marginTop: 4 }}>
                            Cancella firma
                          </button>
                        </div>
                      ) : (
                        <button className="btn-secondary" onClick={() => setShowFirma(true)}>
                          Aggiungi firma
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* BOTTONE SALVA */}
                <button
                  className="btn-primary full-width"
                  onClick={handleSalva}
                  disabled={saving}
                >
                  {saving ? 'Generazione PDF...' : 'Genera PDF e Salva'}
                </button>

              </div>
            )}
          </div>
        </div>
      </div>

      {/* MODAL FIRMA */}
      {showFirma && (
        <div className="firma-overlay">
          <div className="firma-modal">
            <h3>Firma del dipendente</h3>
            <p>Firma con il dito</p>
            <div className="firma-canvas-wrapper">
              <SignatureCanvas
                ref={sigRef}
                canvasProps={{ className: 'firma-canvas' }}
                backgroundColor="white"
              />
            </div>
            <div className="firma-modal-buttons">
              <button className="btn-secondary" onClick={cancellaPad}>Cancella</button>
              <button className="btn-primary" onClick={confermaFirma}>Conferma</button>
              <button className="btn-danger" onClick={() => setShowFirma(false)}>Annulla</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CaricaGiornaliera;
